import { motion } from "framer-motion";
import { useAdminContactRequests } from "@/hooks/useContactRequests";
import { MessageSquare, Loader2, Check, X, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CONTACT_STATUS_LABELS, CONTACT_STATUS_COLORS } from "@/lib/constants";

const URGENCY_COLORS: Record<string, string> = {
  baixa: "bg-muted text-muted-foreground",
  media: "bg-yellow-100 text-yellow-800",
  alta: "bg-red-100 text-red-800",
};

const TYPE_LABELS: Record<string, string> = {
  vaga: "Vaga",
  entrevista_exploratoria: "Entrevista Exploratória",
  mentoria: "Mentoria",
  networking: "Networking",
  proposta: "Proposta",
};

export default function AdminPedidosContato() {
  const navigate = useNavigate();
  const { requests, isLoading, updateStatus } = useAdminContactRequests();

  if (isLoading) {
    return <div className="p-8 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const pending = requests.filter((r) => r.status === "requested");

  const handleApprove = async (id: string) => {
    try {
      await updateStatus.mutateAsync({ id, status: "waiting_student" });
      toast({ title: "Pedido aprovado", description: "O aluno será notificado para aceitar o contato." });
    } catch {
      toast({ title: "Erro ao aprovar", variant: "destructive" });
    }
  };

  const handleReject = async (id: string) => {
    try {
      await updateStatus.mutateAsync({ id, status: "declined", adminNote: "Rejeitado pelo admin" });
      toast({ title: "Pedido rejeitado" });
    } catch {
      toast({ title: "Erro ao rejeitar", variant: "destructive" });
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-2xl font-bold text-foreground">Pedidos de Contato</h1>
        <p className="text-muted-foreground text-sm mt-1">{pending.length} pendentes • {requests.length} total</p>
      </motion.div>

      <div className="mt-6 space-y-3">
        {requests.map((req, i) => (
          <motion.div key={req.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }}
            className={`bg-card rounded-2xl p-5 border ${req.status === "requested" ? "border-primary/30 shadow-sm" : "border-border"}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="font-semibold text-foreground text-sm">
                    {(req.partner_orgs as any)?.name ?? "Parceiro"} → {(req.profiles as any)?.full_name ?? "Aluno"}
                  </h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${CONTACT_STATUS_COLORS[req.status] ?? "bg-muted text-muted-foreground"}`}>
                    {CONTACT_STATUS_LABELS[req.status] ?? req.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <span className="text-xs text-muted-foreground">
                    {TYPE_LABELS[(req as any).contact_type] ?? (req as any).contact_type}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${URGENCY_COLORS[(req as any).urgency] ?? ""}`}>
                    {(req as any).urgency === "alta" ? "🔴 Alta" : (req as any).urgency === "media" ? "🟡 Média" : "⚪ Baixa"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(req.created_at), "dd/MM/yyyy HH:mm")}
                  </span>
                </div>
                {req.message && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{req.message}</p>}
              </div>
              {req.status === "requested" ? (
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/admin/pedidos-contato/${req.id}`)}>
                    <Eye className="w-4 h-4 mr-1" /> Detalhe
                  </Button>
                  <Button size="sm" onClick={() => handleApprove(req.id)}>
                    <Check className="w-4 h-4 mr-1" /> Aprovar
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleReject(req.id)}>
                    <X className="w-4 h-4 mr-1" /> Rejeitar
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/pedidos-contato/${req.id}`)}>
                  <Eye className="w-4 h-4 mr-1" /> Ver
                </Button>
              )}
            </div>
          </motion.div>
        ))}
        {requests.length === 0 && (
          <div className="bg-muted/50 rounded-2xl p-12 text-center">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-foreground mb-2">Nenhum pedido de contato</h3>
            <p className="text-sm text-muted-foreground">Quando parceiros solicitarem contato com talentos, os pedidos aparecerão aqui.</p>
          </div>
        )}
      </div>
    </div>
  );
}
